/**
 * Session Management Utility
 * Handles session expiry detection and redirects with saved property and default dates
 */

// Session configuration
const SESSION_CONFIG = {
  SESSION_TIMEOUT: 10 * 60 * 1000, // 10 minutes in milliseconds
  CHECK_INTERVAL: 60 * 1000, // Check every minute
  STORAGE_KEYS: {
    LAST_ACTIVITY: 'lastActivity',
    PROPERTY_CODE: 'selectedProperty',
    SESSION_ID: 'booking_session_id',
    AUTH_TOKEN: 'authToken'
  }
};

/**
 * Get current timestamp
 */
const getCurrentTimestamp = () => new Date().getTime();

/**
 * Update last activity timestamp
 */
export const updateLastActivity = () => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY, getCurrentTimestamp().toString());
    } catch (error) {
      console.warn('Failed to update last activity:', error);
    }
  }
};

/**
 * Get last activity timestamp
 */
export const getLastActivity = () => {
  if (typeof window !== 'undefined') {
    try {
      const lastActivity = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY);
      return lastActivity ? parseInt(lastActivity, 10) : getCurrentTimestamp();
    } catch (error) {
      console.warn('Failed to get last activity:', error);
      return getCurrentTimestamp();
    }
  }
  return getCurrentTimestamp();
};

/**
 * Check if session has expired
 */
export const isSessionExpired = () => {
  const lastActivity = getLastActivity();
  const currentTime = getCurrentTimestamp();
  const timeDiff = currentTime - lastActivity;
  
  return timeDiff > SESSION_CONFIG.SESSION_TIMEOUT;
};

/**
 * Get saved property from storage
 * Returns PMH as default if no property is saved
 * This ensures PMH is always the fallback property
 */
export const getSavedProperty = () => {
  if (typeof window !== 'undefined') {
    try {
      const savedProperty = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.PROPERTY_CODE);
      if (savedProperty) {
        const property = JSON.parse(savedProperty);
        if (property?.code) {
          console.log('Retrieved saved property from localStorage:', property.code);
          return property.code;
        }
      }
    } catch (error) {
      console.warn('Failed to get saved property:', error);
    }
  }
  console.log('No saved property found, defaulting to PMH');
  return 'PMH'; // Return PMH as default fallback
};

/**
 * Save property to storage - saves whenever property is valid and different
 * Automatically saves property to localStorage for session persistence
 */
export const saveProperty = (propertyCode, description = '') => {
  if (typeof window !== 'undefined') {
    try {
      // Validate property code
      if (!propertyCode || propertyCode === 'null' || propertyCode === 'undefined') {
        console.warn('Invalid property code, not saving:', propertyCode);
        return;
      }
      
      // Check if we already have this property saved
      const existingProperty = getSavedProperty();
      if (existingProperty === propertyCode) {
        console.log('Property already saved, skipping duplicate save:', propertyCode);
        return;
      }
      
      const propertyData = {
        code: propertyCode,
        description: description || propertyCode,
        savedAt: getCurrentTimestamp()
      };
      localStorage.setItem(SESSION_CONFIG.STORAGE_KEYS.PROPERTY_CODE, JSON.stringify(propertyData));
      console.log('✓ Property saved to localStorage:', propertyCode);
    } catch (error) {
      console.warn('Failed to save property:', error);
    }
  }
};

/**
 * Format date as YYYY-MM-DD
 */
const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get default dates (today check-in, tomorrow check-out)
 */
export const getDefaultDates = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  return {
    checkIn: formatDate(today),
    checkOut: formatDate(tomorrow)
  };
};

/**
 * Build redirect URL with property that was accessed/overridden and default dates
 * Uses the property that was actually accessed, not always PMH
 * Priority: 1) Current URL property (if valid), 2) Saved property in localStorage, 3) DEMO fallback
 */
export const buildRedirectUrl = (baseUrl = null) => {
  let propertyToUse = 'PMH'; // Final fallback
  let propertySource = 'default';
  
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const urlProperty = urlParams.get('property');
    
    // Check if URL property is valid (not null, undefined, or empty)
    if (urlProperty && urlProperty !== 'null' && urlProperty !== 'undefined' && urlProperty.trim() !== '') {
      // Priority 1: If URL has valid property, use it - this is the property that was accessed
      propertyToUse = urlProperty;
      propertySource = 'URL';
      console.log('✓ Session redirect: Using current URL property:', urlProperty);
    } else {
      // Priority 2: If no valid URL property, use saved property from localStorage
      // Try both methods to get saved property
      let savedProperty = getSavedProperty();
      
      // If getSavedProperty returns null, try reading directly from localStorage
      if (!savedProperty) {
        try {
          const storedData = localStorage.getItem('selectedProperty');
          if (storedData) {
            const parsed = JSON.parse(storedData);
            savedProperty = parsed.code || parsed;
            console.log('✓ Retrieved property from localStorage (direct):', savedProperty);
          }
        } catch (e) {
          console.warn('Error reading property from localStorage:', e);
        }
      }
      
      if (savedProperty) {
        propertyToUse = savedProperty;
        propertySource = 'localStorage';
        console.log('✓ Session redirect: Using saved property from localStorage:', savedProperty);
      } else {
        // Priority 3: Final fallback to PMH
        propertyToUse = 'PMH';
        propertySource = 'fallback';
        console.log('⚠ Session redirect: No property found in URL or localStorage, using PMH fallback');
        // Save PMH to localStorage for future use
        try {
          localStorage.setItem('selectedProperty', JSON.stringify({
            code: 'PMH',
            description: 'PMH',
            timestamp: Date.now()
          }));
          console.log('✓ PMH saved to localStorage as default property');
        } catch (e) {
          console.warn('Error saving PMH to localStorage:', e);
        }
      }
    }
  }
  
  const { checkIn, checkOut } = getDefaultDates();
  
  // Use current URL as base if not provided
  const currentUrl = baseUrl || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '');
  
  // Build URL with parameters
  const url = new URL(currentUrl);
  url.searchParams.set('checkIn', checkIn);
  url.searchParams.set('checkOut', checkOut);
  url.searchParams.set('adults', '2');
  url.searchParams.set('children', '0');
  url.searchParams.set('room', '1');
  url.searchParams.set('property', propertyToUse);
  url.searchParams.set('sort', 'price-asc');
  
  console.log(`✓ Session redirect URL built: Property=${propertyToUse} (source: ${propertySource})`);
  console.log(`  Full URL: ${url.toString()}`);
  
  return url.toString();
};

/**
 * Clear session data
 */
export const clearSessionData = () => {
  if (typeof window !== 'undefined') {
    try {
      // Clear session-related data but keep property
      localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY);
      localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.SESSION_ID);
      
      // Clear auth token if exists
      localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.AUTH_TOKEN);
      
      // Clear any cached data
      sessionStorage.clear();
      
      console.log('Session data cleared');
    } catch (error) {
      console.warn('Failed to clear session data:', error);
    }
  }
};

/**
 * Handle session expiry - redirect to appropriate URL
 * Maintains the current property (PMH, PKS, etc.) when redirecting
 */
export const handleSessionExpiry = (customRedirectUrl = null) => {
  console.log('🔔 Session expired, handling redirect...');
  
  // Log current property before clearing
  let currentProperty = null;
  if (typeof window !== 'undefined') {
    const currentUrlParams = new URLSearchParams(window.location.search);
    currentProperty = currentUrlParams.get('property');
    console.log(`  Current property in URL: ${currentProperty || 'none'}`);
    
    // IMPORTANT: Ensure property is saved to localStorage before clearing session
    if (currentProperty && currentProperty !== 'null' && currentProperty !== 'undefined' && currentProperty.trim() !== '') {
      try {
        localStorage.setItem('selectedProperty', JSON.stringify({
          code: currentProperty,
          description: currentProperty,
          timestamp: Date.now()
        }));
        console.log('✓ Property saved before session clear:', currentProperty);
      } catch (e) {
        console.warn('Error saving property before session clear:', e);
      }
    }
    
    // Verify what's in localStorage
    try {
      const storedData = localStorage.getItem('selectedProperty');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        console.log('✓ Property in localStorage:', parsed.code);
      } else {
        console.warn('⚠ No property found in localStorage');
      }
    } catch (e) {
      console.warn('Error reading property from localStorage:', e);
    }
  }
  
  // Clear expired session data (but keep property)
  clearSessionData();
  
  // Build redirect URL with current/saved property
  const redirectUrl = customRedirectUrl || buildRedirectUrl();
  
  console.log('🔄 Redirecting to maintain property:', redirectUrl);
  
  // Perform redirect
  if (typeof window !== 'undefined') {
    window.location.href = redirectUrl;
  }
};

/**
 * Initialize session monitoring
 */
export const initializeSessionMonitoring = (onSessionExpiry = null) => {
  if (typeof window === 'undefined') return null;
  
  // Update activity on page load
  updateLastActivity();
  
  // Set up activity listeners
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  
  const handleActivity = () => {
    updateLastActivity();
  };
  
  // Add event listeners
  activityEvents.forEach(event => {
    document.addEventListener(event, handleActivity, true);
  });
  
  // Set up periodic session check
  const sessionCheckInterval = setInterval(() => {
    if (isSessionExpired()) {
      console.log('Session expired detected');
      clearInterval(sessionCheckInterval);
      
      // Remove activity listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      // Handle expiry
      if (onSessionExpiry && typeof onSessionExpiry === 'function') {
        onSessionExpiry();
      } else {
        handleSessionExpiry();
      }
    }
  }, SESSION_CONFIG.CHECK_INTERVAL);
  
  // Return cleanup function
  return () => {
    clearInterval(sessionCheckInterval);
    activityEvents.forEach(event => {
      document.removeEventListener(event, handleActivity, true);
    });
  };
};

/**
 * Check if user is authenticated (has valid session)
 */
export const isAuthenticated = () => {
  if (typeof window === 'undefined') return false;
  
  try {
    const authToken = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.AUTH_TOKEN);
    const sessionId = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.SESSION_ID);
    
    // Check if we have auth data and session is not expired
    return (authToken || sessionId) && !isSessionExpired();
  } catch (error) {
    console.warn('Failed to check authentication status:', error);
    return false;
  }
};

/**
 * Extend session (reset last activity)
 */
export const extendSession = () => {
  updateLastActivity();
  console.log('Session extended');
};

/**
 * Get session time remaining in minutes
 */
export const getSessionTimeRemaining = () => {
  const lastActivity = getLastActivity();
  const currentTime = getCurrentTimestamp();
  const timeDiff = currentTime - lastActivity;
  const timeRemaining = SESSION_CONFIG.SESSION_TIMEOUT - timeDiff;
  
  return Math.max(0, Math.floor(timeRemaining / (60 * 1000))); // Return minutes
};

/**
 * Check if session is about to expire (within 2 minutes)
 */
export const isSessionAboutToExpire = () => {
  const timeRemaining = getSessionTimeRemaining();
  return timeRemaining <= 2 && timeRemaining > 0;
};

export default {
  updateLastActivity,
  getLastActivity,
  isSessionExpired,
  getSavedProperty,
  saveProperty,
  getDefaultDates,
  buildRedirectUrl,
  clearSessionData,
  handleSessionExpiry,
  initializeSessionMonitoring,
  isAuthenticated,
  extendSession,
  getSessionTimeRemaining,
  isSessionAboutToExpire
};
